import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import empreendimentoService from "../services/empreendimento";
import { ESTADOS_BRASIL, GRUPO_ATIVIDADE_OPTIONS, ATIVIDADE_POR_GRUPO, UNIDADE_MEDIDA_AREA_OPTIONS } from "../utils/constants";
import { formatCEP } from "../utils/formatters";
import { limpar } from "../utils/utils";
import CharCounter from "../components/CharCounter";

const CHAR_LIMITS = {
  nome: 100,
  descricao: 500,
  endereco: 100,
  municipio: 50,
};

function Empreendimentos() {
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [modalError, setModalError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEmpreendimento, setSelectedEmpreendimento] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [filtros, setFiltros] = useState({ nome: "", descricao: "" });
  const [filtrosDebounced, setFiltrosDebounced] = useState({
    nome: "",
    descricao: "",
  });
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);
  const [totalEmpreendimentos, setTotalEmpreendimentos] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [formData, setFormData] = useState({
    nome: "",
    area: "",
    unidadeMedidaArea: 0,
    descricao: "",
    status: "ATIVO",
    endereco: "",
    numero: "",
    municipio: "",
    estado: "",
    cep: "",
    latitude: "",
    longitude: "",
    cnpj: "",
    grupoAtividade: "",
    atividadeEmpreendimento: "",
    cadastrarComCpf: false,
    mesmoEnderecoSede: true,
    enderecoAtividade: "",
    numeroAtividade: "",
    municipioAtividade: "",
    estadoAtividade: "",
    cepAtividade: "",
  });
  const [semNumero, setSemNumero] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const statusMap = {
    ATIVO: 0,
    INATIVO: 1,
    SUSPENSO: 2,
    CANCELADO: 3,
  };
  const statusReverseMap = {
    0: "ATIVO",
    1: "INATIVO",
    2: "SUSPENSO",
    3: "CANCELADO",
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFiltrosDebounced({
        nome: (filtros.nome || "").trim(),
        descricao: (filtros.descricao || "").trim(),
      });
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [filtros]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [filtrosDebounced]);

  useEffect(() => {
    carregarEmpreendimentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosDebounced, paginaAtual, itensPorPagina]);

  useEffect(() => {
    if (!formData.mesmoEnderecoSede) return;
    setFormData(prev => ({
      ...prev,
      enderecoAtividade: prev.endereco,
      numeroAtividade: prev.numero,
      municipioAtividade: prev.municipio,
      estadoAtividade: prev.estado,
      cepAtividade: prev.cep,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.endereco, formData.numero, formData.municipio, formData.estado, formData.cep, formData.mesmoEnderecoSede]);

  const carregarEmpreendimentos = async () => {
    setLoading(true);
    try {
      const resultado = await empreendimentoService.listarPaginado(
        filtrosDebounced,
        paginaAtual,
        itensPorPagina,
      );
      setEmpreendimentos(resultado.items);
      setTotalEmpreendimentos(resultado.totalCount || 0);
      setTotalPaginas(resultado.totalPages || 0);
    } catch (err) {
      setError(
        err.response?.data?.message || err.detail || "Erro ao carregar dados.",
      );
      setEmpreendimentos([]);
      setTotalEmpreendimentos(0);
      setTotalPaginas(0);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMenu = (event, id) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedMenuId(id);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setSelectedMenuId(null);
  };

  const handleOpenDialog = (empreendimento = null) => {
    setSelectedEmpreendimento(empreendimento);
    if (empreendimento) {
      const numero = empreendimento.endereco.numero;
      setSemNumero(numero == null || numero === "");
      setFormData({
        ...empreendimento,
        enderecoId: empreendimento.endereco.id,
        endereco: empreendimento.endereco.rua,
        numero: numero != null ? String(numero) : "",
        municipio: empreendimento.endereco.municipio,
        estado: empreendimento.endereco.estado,
        cep: empreendimento.endereco.cep,
        status: statusReverseMap[empreendimento.status],
        unidadeMedidaArea: empreendimento.unidadeMedidaArea ?? 0,
        grupoAtividade: empreendimento.grupoAtividade != null ? empreendimento.grupoAtividade : "",
        atividadeEmpreendimento: empreendimento.atividadeEmpreendimento != null ? empreendimento.atividadeEmpreendimento : "",
        enderecoAtividadeId: empreendimento.enderecoAtividade?.id ?? null,
        enderecoAtividade: empreendimento.enderecoAtividade?.rua ?? "",
        numeroAtividade: empreendimento.enderecoAtividade?.numero != null ? String(empreendimento.enderecoAtividade.numero) : "",
        municipioAtividade: empreendimento.enderecoAtividade?.municipio ?? "",
        estadoAtividade: empreendimento.enderecoAtividade?.estado ?? "",
        cepAtividade: empreendimento.enderecoAtividade?.cep != null ? String(empreendimento.enderecoAtividade.cep) : "",
        mesmoEnderecoSede: empreendimento.enderecoAtividade == null,
        cadastrarComCpf: empreendimento.cadastrarComCpf ?? false,
      });
    } else {
      setSemNumero(false);
      setFormData({
        nome: "",
        area: "",
        unidadeMedidaArea: 0,
        descricao: "",
        status: "ATIVO",
        cnpj: "",
        endereco: "",
        numero: "",
        municipio: "",
        estado: "",
        cep: "",
        grupoAtividade: "",
        atividadeEmpreendimento: "",
        cadastrarComCpf: false,
        mesmoEnderecoSede: true,
        enderecoAtividade: "",
        numeroAtividade: "",
        municipioAtividade: "",
        estadoAtividade: "",
        cepAtividade: "",
      });
    }
    setModalError("");
    setOpenDialog(true);
    handleCloseMenu();
  };

  const formatErrorMessage = (err) => {
    const responseData = err.response?.data;
    let errorsObj = null;

    if (
      responseData?.errors &&
      typeof responseData.errors === "object" &&
      Object.keys(responseData.errors).length > 0
    ) {
      errorsObj = responseData.errors;
    } else if (
      responseData?.detail?.errors &&
      typeof responseData.detail.errors === "object"
    ) {
      errorsObj = responseData.detail.errors;
    }

    if (errorsObj) {
      const errorMessages = Object.entries(errorsObj)
        .map(([field, messages]) => {
          const fieldNames = {
            nome: "Nome",
            area: "Área",
            descricao: "Descrição",
            status: "Status",
            endereco: "Endereço",
            numero: "Número",
            municipio: "Município",
            estado: "Estado",
            cep: "CEP",
            coordenadas: "Coordenadas",
            CNPJ: formData.cadastrarComCpf ? "CPF do Empreendedor" : "CNPJ",
            grupoAtividade: "Grupo de Atividade",
            atividadeEmpreendimento: "Atividade",
          };

          const fieldName = fieldNames[field] || field;
          const message = Array.isArray(messages) ? messages[0] : messages;
          return `${fieldName}: ${message}`;
        })
        .join("\n");

      if (errorMessages) return errorMessages;
    }

    return (
      responseData?.detail ||
      responseData?.message ||
      err.message ||
      "Ocorreu um erro ao processar sua solicitação."
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setModalError("");

    try {
      const dadosFormatados = {
        ...formData,
        id: selectedEmpreendimento ? selectedEmpreendimento.id : null,
        area: parseFloat(formData.area),
        unidadeMedidaArea: formData.unidadeMedidaArea,
        status: statusMap[formData.status],
        grupoAtividade: formData.grupoAtividade,
        atividadeEmpreendimento: formData.atividadeEmpreendimento,
        endereco: {
          id: formData.enderecoId,
          rua: formData.endereco,
          numero: semNumero ? null : formData.numero ? String(formData.numero) : null,
          municipio: formData.municipio,
          estado: formData.estado,
          cep: limpar(formData.cep),
        },
        enderecoAtividade: formData.mesmoEnderecoSede
          ? null
          : (formData.enderecoAtividade
            ? {
                id: formData.enderecoAtividadeId ?? null,
                rua: formData.enderecoAtividade,
                numero: formData.numeroAtividade ? String(formData.numeroAtividade) : null,
                municipio: formData.municipioAtividade,
                estado: formData.estadoAtividade,
                cep: limpar(formData.cepAtividade),
              }
            : null),
        mesmoEnderecoSede: formData.mesmoEnderecoSede,
        cadastrarComCpf: formData.cadastrarComCpf,
      };

      if (selectedEmpreendimento) {
        await empreendimentoService.atualizar(
          selectedEmpreendimento.id,
          dadosFormatados,
        );
      } else {
        await empreendimentoService.criar(dadosFormatados);
      }
      setOpenDialog(false);
      setPaginaAtual(1);
      carregarEmpreendimentos();
    } catch (err) {
      if (err.response?.status === 401) {
        throw err;
      }
      setModalError(formatErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Deseja realmente excluir este empreendimento?")) {
      try {
        await empreendimentoService.excluir(id);
        setPaginaAtual(1);
        carregarEmpreendimentos();
        handleCloseMenu();
      } catch (err) {
        setError(err.detail || "Erro ao excluir empreendimento");
      }
    }
  };

  const handleExibir = (id) => {
    window.open(`/empreendimento/${id}`);
    handleCloseMenu();
  };

  const buscarCep = async (cepFormatado) => {
    const digits = cepFormatado.replace(/\D/g, "");
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setFormData((prev) => ({
          ...prev,
          endereco: data.logradouro || prev.endereco,
          municipio: data.localidade || prev.municipio,
          estado: data.uf || prev.estado,
        }));
      }
    } catch {
    } finally {
      setCepLoading(false);
    }
  };

  const handleCEPChange = (e) => {
    const formattedCEP = formatCEP(e.target.value);
    setFormData({ ...formData, cep: formattedCEP });
    buscarCep(formattedCEP);
  };

  const buscarCepAtividade = async (cepFormatado) => {
    const digits = cepFormatado.replace(/\D/g, "");
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setFormData((prev) => ({
          ...prev,
          enderecoAtividade: data.logradouro || prev.enderecoAtividade,
          municipioAtividade: data.localidade || prev.municipioAtividade,
          estadoAtividade: data.uf || prev.estadoAtividade,
        }));
      }
    } catch {
    } finally {
      setCepLoading(false);
    }
  };

  const handleCEPAtividadeChange = (e) => {
    const formattedCEP = formatCEP(e.target.value);
    setFormData({ ...formData, cepAtividade: formattedCEP });
    buscarCepAtividade(formattedCEP);
  };

  return (
    <Container sx={{ px: { xs: 2, sm: 3 } }}>
      <Box sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem", md: "2.125rem" } }}
          >
            Empreendimentos
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Novo Empreendimento
          </Button>
        </Stack>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              "& .MuiAlert-message": {
                whiteSpace: "pre-line",
              },
            }}
          >
            {error}
          </Alert>
        )}

        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Grid container spacing={{ xs: 2, md: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Buscar por Nome"
                value={filtros.nome}
                onChange={(e) =>
                  setFiltros({ ...filtros, nome: e.target.value })
                }
                inputProps={{ maxLength: 100 }}
                InputProps={{
                  type: "search",
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Buscar por Descrição"
                value={filtros.descricao}
                onChange={(e) =>
                  setFiltros({ ...filtros, descricao: e.target.value })
                }
                inputProps={{ maxLength: 500 }}
                InputProps={{
                  type: "search",
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Área</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Opções</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : empreendimentos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Nenhum registro encontrado
                  </TableCell>
                </TableRow>
              ) : (
                empreendimentos.map((empreendimento) => (
                  <TableRow key={empreendimento.id}>
                    <TableCell>{empreendimento.nome}</TableCell>
                    <TableCell>
                      {empreendimento.area} {UNIDADE_MEDIDA_AREA_OPTIONS.find((o) => o.value === (empreendimento.unidadeMedidaArea ?? 0))?.label ?? "ha"}
                    </TableCell>
                    <TableCell>{empreendimento.descricao}</TableCell>
                    <TableCell>
                      {statusReverseMap[empreendimento.status]}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        aria-label="Opções do empreendimento"
                        onClick={(e) => handleOpenMenu(e, empreendimento.id)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalEmpreendimentos}
          page={Math.max(0, paginaAtual - 1)}
          onPageChange={(_, newPage) => setPaginaAtual(newPage + 1)}
          rowsPerPage={itensPorPagina}
          onRowsPerPageChange={(event) => {
            setItensPorPagina(parseInt(event.target.value, 10));
            setPaginaAtual(1);
          }}
          rowsPerPageOptions={[5, 10, 20, 50]}
          labelRowsPerPage="Itens por página"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count} | Página ${paginaAtual}${totalPaginas > 0 ? ` de ${totalPaginas}` : ""}`
          }
        />

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleCloseMenu}
        >
          <MenuItem onClick={() => handleExibir(selectedMenuId)}>
            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
            Exibir
          </MenuItem>
          <MenuItem
            onClick={() =>
              handleOpenDialog(
                empreendimentos.find((e) => e.id === selectedMenuId),
              )
            }
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Alterar
          </MenuItem>
          <MenuItem onClick={() => handleDelete(selectedMenuId)}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Excluir
          </MenuItem>
        </Menu>

        <Dialog
          open={openDialog}
          onClose={() => { setOpenDialog(false); setModalError(""); }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {selectedEmpreendimento
              ? "Editar Empreendimento"
              : "Novo Empreendimento"}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              {modalError && (
                <Alert
                  severity="error"
                  sx={{ mb: 2, "& .MuiAlert-message": { whiteSpace: "pre-line" } }}
                  onClose={() => setModalError("")}
                >
                  {modalError}
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Nome"
                    fullWidth
                    required
                    value={formData.nome}
                    onChange={(e) =>
                      setFormData({ ...formData, nome: e.target.value })
                    }
                    inputProps={{ maxLength: CHAR_LIMITS.nome }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <CharCounter
                            value={formData.nome}
                            maxLength={CHAR_LIMITS.nome}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={8} md={4}>
                  <TextField
                    label="Área"
                    type="number"
                    fullWidth
                    required
                    value={formData.area}
                    inputProps={{
                      step: "0.01",
                      min: "0",
                    }}
                    onChange={(e) =>
                      setFormData({ ...formData, area: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={4} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Unidade</InputLabel>
                    <Select
                      value={formData.unidadeMedidaArea}
                      onChange={(e) =>
                        setFormData({ ...formData, unidadeMedidaArea: e.target.value })
                      }
                      label="Unidade"
                    >
                      {UNIDADE_MEDIDA_AREA_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Status"
                    fullWidth
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                  >
                    <MenuItem value="ATIVO">Ativo</MenuItem>
                    <MenuItem value="INATIVO">Inativo</MenuItem>
                    <MenuItem value="SUSPENSO">Suspenso</MenuItem>
                    <MenuItem value="CANCELADO">Cancelado</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Descrição"
                    multiline
                    rows={4}
                    fullWidth
                    required
                    value={formData.descricao}
                    onChange={(e) =>
                      setFormData({ ...formData, descricao: e.target.value })
                    }
                    inputProps={{ maxLength: CHAR_LIMITS.descricao }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 0.5,
                    }}
                  >
                    <CharCounter
                      value={formData.descricao}
                      maxLength={CHAR_LIMITS.descricao}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Endereço"
                    fullWidth
                    required
                    value={formData.endereco}
                    onChange={(e) =>
                      setFormData({ ...formData, endereco: e.target.value })
                    }
                    inputProps={{ maxLength: CHAR_LIMITS.endereco }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <CharCounter
                            value={formData.endereco}
                            maxLength={CHAR_LIMITS.endereco}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Número"
                    value={formData.numero}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setFormData({ ...formData, numero: val });
                    }}
                    disabled={semNumero}
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={semNumero}
                        onChange={(e) => {
                          setSemNumero(e.target.checked);
                          if (e.target.checked)
                            setFormData({ ...formData, numero: "" });
                        }}
                      />
                    }
                    label="Sem número"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Município"
                    fullWidth
                    required
                    value={formData.municipio}
                    onChange={(e) =>
                      setFormData({ ...formData, municipio: e.target.value })
                    }
                    inputProps={{ maxLength: CHAR_LIMITS.municipio }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <CharCounter
                            value={formData.municipio}
                            maxLength={CHAR_LIMITS.municipio}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Estado"
                    fullWidth
                    required
                    value={formData.estado}
                    onChange={(e) =>
                      setFormData({ ...formData, estado: e.target.value })
                    }
                  >
                    {ESTADOS_BRASIL.map((estado) => (
                      <MenuItem key={estado.sigla} value={estado.sigla}>
                        {estado.nome} ({estado.sigla})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="CEP"
                    fullWidth
                    required
                    value={formatCEP(formData.cep)}
                    onChange={handleCEPChange}
                    inputProps={{
                      maxLength: 9,
                      placeholder: "00000-000",
                    }}
                    InputProps={{
                      endAdornment: cepLoading ? (
                        <InputAdornment position="end">
                          <CircularProgress size={18} />
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.cadastrarComCpf}
                        onChange={(e) =>
                          setFormData({ ...formData, cadastrarComCpf: e.target.checked, cnpj: "" })
                        }
                        disabled={!!selectedEmpreendimento}
                      />
                    }
                    label="Realizar cadastro do empreendimento com o CPF do empreendedor"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={formData.cadastrarComCpf ? "CPF do Empreendedor" : "CNPJ"}
                    fullWidth
                    required
                    value={formData.cnpj}
                    onChange={(e) => {
                      if (formData.cadastrarComCpf) {
                        const value = e.target.value.replace(/\D/g, "").slice(0, 11);
                        let formatted = value;
                        if (value.length > 3) formatted = value.slice(0, 3) + "." + value.slice(3);
                        if (value.length > 6) formatted = formatted.slice(0, 7) + "." + value.slice(6);
                        if (value.length > 9) formatted = formatted.slice(0, 11) + "-" + value.slice(9);
                        setFormData({ ...formData, cnpj: formatted });
                      } else {
                        const value = e.target.value.replace(/\D/g, "").slice(0, 14);
                        let formatted = value;
                        if (value.length > 2)
                          formatted = value.slice(0, 2) + "." + value.slice(2);
                        if (value.length > 5)
                          formatted = formatted.slice(0, 6) + "." + value.slice(5);
                        if (value.length > 8)
                          formatted = formatted.slice(0, 10) + "/" + value.slice(8);
                        if (value.length > 12)
                          formatted = formatted.slice(0, 15) + "-" + value.slice(12);
                        setFormData({ ...formData, cnpj: formatted });
                      }
                    }}
                    inputProps={{
                      maxLength: formData.cadastrarComCpf ? 14 : 18,
                      placeholder: formData.cadastrarComCpf ? "000.000.000-00" : "00.000.000/0000-00",
                    }}
                    disabled={!!selectedEmpreendimento}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Grupo de Atividade"
                    fullWidth
                    required
                    value={formData.grupoAtividade !== "" ? formData.grupoAtividade : ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData({
                        ...formData,
                        grupoAtividade: val,
                        atividadeEmpreendimento: "",
                      });
                    }}
                    disabled={!!selectedEmpreendimento}
                  >
                    {GRUPO_ATIVIDADE_OPTIONS.map((g) => (
                      <MenuItem key={g.value} value={g.value}>
                        {g.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Atividade do Empreendimento"
                    fullWidth
                    required
                    value={formData.atividadeEmpreendimento !== "" ? formData.atividadeEmpreendimento : ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        atividadeEmpreendimento: e.target.value,
                      })
                    }
                    disabled={formData.grupoAtividade === "" || !!selectedEmpreendimento}
                  >
                    {(ATIVIDADE_POR_GRUPO[formData.grupoAtividade] || []).map(
                      (a) => (
                        <MenuItem key={a.value} value={a.value}>
                          {a.label}
                        </MenuItem>
                      ),
                    )}
                  </TextField>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" fontWeight="bold" mt={3} mb={1}>
                Endereço onde as atividades serão realizadas
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.mesmoEnderecoSede}
                        onChange={(e) =>
                          setFormData({ ...formData, mesmoEnderecoSede: e.target.checked })
                        }
                      />
                    }
                    label="Mesmo endereço do empreendimento"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Endereço"
                    fullWidth
                    value={formData.enderecoAtividade}
                    onChange={(e) =>
                      setFormData({ ...formData, enderecoAtividade: e.target.value })
                    }
                    disabled={formData.mesmoEnderecoSede}
                    inputProps={{ maxLength: CHAR_LIMITS.endereco }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <CharCounter
                            value={formData.enderecoAtividade}
                            maxLength={CHAR_LIMITS.endereco}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Número"
                    value={formData.numeroAtividade}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setFormData({ ...formData, numeroAtividade: val });
                    }}
                    disabled={formData.mesmoEnderecoSede}
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                <Grid item xs={12} sm={3} />
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Município"
                    fullWidth
                    value={formData.municipioAtividade}
                    onChange={(e) =>
                      setFormData({ ...formData, municipioAtividade: e.target.value })
                    }
                    disabled={formData.mesmoEnderecoSede}
                    inputProps={{ maxLength: CHAR_LIMITS.municipio }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <CharCounter
                            value={formData.municipioAtividade}
                            maxLength={CHAR_LIMITS.municipio}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Estado"
                    fullWidth
                    value={formData.estadoAtividade}
                    onChange={(e) =>
                      setFormData({ ...formData, estadoAtividade: e.target.value })
                    }
                    disabled={formData.mesmoEnderecoSede}
                  >
                    {ESTADOS_BRASIL.map((estado) => (
                      <MenuItem key={estado.sigla} value={estado.sigla}>
                        {estado.nome} ({estado.sigla})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="CEP"
                    fullWidth
                    value={formatCEP(formData.cepAtividade)}
                    onChange={handleCEPAtividadeChange}
                    disabled={formData.mesmoEnderecoSede}
                    inputProps={{
                      maxLength: 9,
                      placeholder: "00000-000",
                    }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? "Salvando..." : "Salvar"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Box>
    </Container>
  );
}

export default Empreendimentos;
